import React, { useCallback } from 'react';
import { createFormField } from 'Shared/utils/createFormField';
import pick from 'lodash/pick';
import cn from 'classnames';

import './index.scss';
import { WpIcon } from 'Wpds/components/icon';

function defaultOnShowError({ touched, error, errorLabel }) {
  return touched && error && errorLabel;
}

type Props = {
  className?: string;
  inputClassName?: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  labelMuted?: boolean;
  label?: string;
  value: string;
  placeholder?: string;
  icon?: React.ReactNode;
  cleared?: boolean;
  clearIcon?: React.ReactNode;
  error?: string;
  name?: string;
  touched?: boolean;
  required?: boolean;
  labelRequired?: string;
  onFocus: Function;
  onBlur: Function;
  onInputFocus?: Function;
  onShowError?: Function;
  autoFocus?: boolean;
  onKeyPress?: Function;
  onKeyDown?: Function;
  innerRef?: {
    current: HTMLDivElement;
  };
  errorLabel?: boolean;
};

const InputWhiteProps = Object.freeze([
  'onChange',
  'name',
  'value',
  'placeholder',
  'error',
  'onBlur',
  'autoFocus',
  'autoComplete',
  'type',
]);

export function Input(props: Props) {
  const {
    className = '',
    inputClassName = '',
    icon,
    cleared = false,
    clearIcon = <WpIcon name="times-circle" />,
    error,
    labelMuted,
    label,
    touched,
    onChange,
    onShowError = defaultOnShowError,
    onFocus,
    required,
    labelRequired = '*',
    onBlur,
    onKeyPress,
    onKeyDown,
    innerRef,
    value,
    onInputFocus,
    errorLabel = true,
  } = props;
  const clearedProps = pick(props, InputWhiteProps);

  const handleClear = useCallback(() => {
    onChange(null);
    setTimeout(onFocus);
    setTimeout(onBlur);
  }, [onBlur, onChange, onFocus]);

  const handleKeyPress = useCallback(
    event => {
      if (onKeyPress) onKeyPress(event);
      if (event.key === 'Enter') {
        onFocus();
        onBlur();
      }
    },
    [onFocus, onBlur, onKeyPress]
  );

  const handleFocus = useCallback(() => {
    if (onInputFocus) onInputFocus();
    onFocus();
  }, [onFocus, onInputFocus]);

  return (
    <div className={`${className} form-input w-100`}>
      {label && (
        <span
          className={cn('form-input-label d-flex', {
            'form-input-label--muted': labelMuted,
            'text-danger': touched && error,
          })}
        >
          {label}
          {required ? <span className="text-danger ml-2">{labelRequired}</span> : null}
        </span>
      )}
      <div className="position-relative m-0">
        <input
          ref={innerRef}
          className={cn(`${inputClassName} form-control w-100`, {
            'pl-5': icon,
            'pr-4': cleared,
            'is-invalid': touched && error,
          })}
          placeholder="Seach Perform..."
          onKeyDown={onKeyDown}
          onKeyPress={handleKeyPress}
          onFocus={handleFocus}
          {...clearedProps}
        />
        {icon ? <div className="position-absolute form-input-icon">{icon}</div> : null}
        {cleared && value ? (
          <div className="position-absolute form-input-clear-icon" onClick={handleClear}>
            {clearIcon}
          </div>
        ) : null}
      </div>
      {onShowError({ touched, error, errorLabel, value }) ? (
        <p className="form-input-error text-danger">{error}</p>
      ) : null}
    </div>
  );
}

export default createFormField(Input);
